// Constants for FormBuilder sub-page types and component types
export const FormBuilderSubPageType = ["Edit Record", "New Record"];
export const FormBuilderComponentType = ["Form"];

// Check if the form builder has changed compared to default form builder
export function isStepsChanged(steps, defaultSteps) {
    if (steps.length !== defaultSteps.length) return true;
    for (let index = 0; index < steps.length; index++) {
        const step = steps[index];
        const defaultStep = defaultSteps[index];
        if (step.name !== defaultStep.name) {
            return true;
        }
        if (isLanguagesChanged(step.languages, defaultStep.languages, 'name')) {
            return true;
        }
        const isChanged = isLayoutChanged(step.layout, defaultStep.layout);
        if (isChanged) {
            return true;
        }
    }
    return false;
}

// Check if the form actions have changed compared to default form actions
export function isFormActionsChanged(formActions, defaultFormActions) {
    if (formActions.length !== defaultFormActions.length) return true;
    for (let index = 0; index < formActions.length; index++) {
        const formAction = formActions[index];
        const defaultFormAction = defaultFormActions[index];
        if (
            formAction.name !== defaultFormAction.name ||
            formAction.isActionHasIcon !== defaultFormAction.isActionHasIcon ||
            formAction.icon !== defaultFormAction.icon ||
            isLanguagesChanged(formAction.languages, defaultFormAction.languages, 'label')
        ) {
            return true;
        }
    }
    return false;
}

// Check if the languages have changed compared to default languages
function isLanguagesChanged(languages = {}, defaultLanguages = {}, keyToCompare) {
    const keys = Object.keys(languages);
    const defaultKeys = Object.keys(defaultLanguages);
    if (keys.length !== defaultKeys.length) return true;
    for (const key of keys) {
        if (languages[key][keyToCompare] !== defaultLanguages[key][keyToCompare]) {
            return true;
        }
    }
    return false;
}


// Check if the layout has changed compared to default layouts
export function isLayoutChanged(layouts, defaultLayouts) {

    if (layouts.length !== defaultLayouts.length) return true;

    if (areSectionsMoved(layouts, defaultLayouts)) return true;

    for (let index = 0; index < layouts.length; index++) {
        const section = layouts[index];
        const defaultSection = defaultLayouts.find(defaultSection => defaultSection.section === section.section);

        if (areSectionsChanged(section, defaultSection)) {
            return true;
        }

        const columns = section.columns;
        const defaultColumns = defaultSection && defaultSection.columns;

        if (columns.length !== defaultColumns.length) return true;

        for (let index = 0; index < columns.length; index++) {
            const column = columns[index];
            const defaultColumn = defaultColumns.find(defCol => column.attributeId === defCol.attributeId);
            if (areColumnsChanged(column, defaultColumn)) {
                return true;
            }
        }
    }

    return false;
}

// Check if the sections are moved
function areSectionsMoved(layout, defaultLayout) {
    const layoutSections = layout.map((section) => section.section);
    const defaultLayoutSections = defaultLayout.map((section) => section.section);
    return layoutSections.some((section, index) => section !== defaultLayoutSections[index]);
}

// Check if the sections are changed
function areSectionsChanged(section, defaultSection) {
    return (
        !defaultSection ||
        section.section !== defaultSection.section ||
        section.columns.length !== defaultSection.columns.length ||
        isLanguagesChanged(section.languages, defaultSection.languages, 'section')
    );
}

// Check if the columns are changed
function areColumnsChanged(column, defaultColumn) {
    return (
        !defaultColumn ||
        column.w !== defaultColumn.w ||
        column.h !== defaultColumn.h ||
        column.x !== defaultColumn.x ||
        column.y !== defaultColumn.y ||
        column.cellRules !== defaultColumn.cellRules ||
        column.hyperlink !== defaultColumn.hyperlink ||
        column.targetPageId !== defaultColumn.targetPageId ||
        areColumnSettingsChanged(column.columnSettings || {}, defaultColumn.columnSettings || {})
    );
}

// Check if column settings have changed
function areColumnSettingsChanged(currentSettings, defaultSettings) {
    // Get all unique keys from both objects
    const allKeys = [...new Set([...Object.keys(currentSettings), ...Object.keys(defaultSettings)])];

    for (const key of allKeys) {
        const currentValue = currentSettings[key];
        const defaultValue = defaultSettings[key];

        // Check if values are different
        if (currentValue !== defaultValue) {
            // Handle nested objects (like activeColors)
            if (typeof currentValue === 'object' && typeof defaultValue === 'object' &&
                currentValue !== null && defaultValue !== null) {
                if (!areObjectsEqual(currentValue, defaultValue)) {
                    return true;
                }
            } else {
                return true;
            }
        }
    }

    return false;
}

// Helper function to compare nested objects
function areObjectsEqual(obj1, obj2) {
    if (obj1 === obj2) return true;
    if (obj1 === null || obj2 === null) return false;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key)) return false;

        const val1 = obj1[key];
        const val2 = obj2[key];

        if (typeof val1 === 'object' && typeof val2 === 'object' &&
            val1 !== null && val2 !== null) {
            if (!areObjectsEqual(val1, val2)) {
                return false;
            }
        } else if (val1 !== val2) {
            return false;
        }
    }

    return true;
}

// Find a dragged column by its ID within sections
export function findDraggedColumn(sections, draggedColumnId) {
    for (const section of sections) {
        const draggedItem = section.columns.find(column => column.i === draggedColumnId);
        if (draggedItem) return draggedItem;
    }
}

// Find a dragged attribute by its ID within attributes
export function findDraggedAttribute(attributes, draggedAttribute) {
    return attributes.find(attribute => attribute._id === draggedAttribute.attributeId);
}

// Find the section ID containing a dragged column within sections
export function findDraggedColumnSectionId(sections, draggedColumn) {
    return sections.find(section => section.columns.includes(draggedColumn)).id;
}

// Get attributes from a layout based on IDs and entity ID
export function getAttributesFromLayout(layout, attributes, entityId) {
    const componentAttributesIds = [];

    layout.forEach(section => {
        section.columns.forEach(column => componentAttributesIds.push(column.attributeId));
    });

    const componentAttributes = attributes
        .filter(attribute => componentAttributesIds.includes(attribute._id))
        .map(attribute => ({ ...attribute, attributeId: attribute._id, entityId }));
    return componentAttributes || [];
};